import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";

import { Trans, useTranslation } from "react-i18next";

import Particle from "../Particle";

import bizSkillsBadge from "../../Assets/Biz Skills Badge.png";

const CONSULTATION_FORM_URL = "https://forms.gle/YChtAR4AbAkG8nAC9";

function ConsultingAdvisory() {
  const { t } = useTranslation();

  const coreAreas = t("consultingAdvisoryPage.coreAreas.items", { returnObjects: true });
  const coreAreaItems = Array.isArray(coreAreas) ? coreAreas : [];

  const offers = t("consultingAdvisoryPage.howToWork.steps", { returnObjects: true });
  const offerItems = Array.isArray(offers) ? offers : [];

  const buildItemsRaw = t("consultingAdvisoryPage.build.items", { returnObjects: true });
  const buildItems = Array.isArray(buildItemsRaw) ? buildItemsRaw : [];

  const approachBulletsRaw = t("consultingAdvisoryPage.approach.bullets", { returnObjects: true });
  const approachBullets = Array.isArray(approachBulletsRaw) ? approachBulletsRaw : [];

  return (
    <Container fluid className="about-section consulting-advisory-page">
      <Particle />

      <Container className="consulting-advisory-container">
        <header className="consulting-advisory-hero">
          <div className="consulting-advisory-eyebrow">{t("consultingAdvisoryPage.eyebrow")}</div>

          <h1 className="consulting-advisory-hero-title">
            <Trans i18nKey="consultingAdvisoryPage.hero.title" components={{ purple: <span className="purple" /> }} />
          </h1>

          <p className="consulting-advisory-hero-lead">{t("consultingAdvisoryPage.hero.lead")}</p>

          <div className="consulting-advisory-hero-pill">
            <span className="consulting-advisory-pill-label">{t("consultingAdvisoryPage.hero.pillLabel")}</span>
            <span className="consulting-advisory-pill-text">{t("consultingAdvisoryPage.hero.pillText")}</span>
          </div>

          <div className="consulting-advisory-hero-cta" aria-label={t("consultingAdvisoryPage.a11y.ctaLabel")}>
            <Button
              as="a"
              href={CONSULTATION_FORM_URL}
              target="_blank"
              rel="noreferrer"
              className="custom-yellow-btn consulting-advisory-btn"
            >
              {t("consultingAdvisoryPage.cta.book")}
            </Button>
          </div>

          <div className="consulting-advisory-hero-badge">
            <img
              src={bizSkillsBadge}
              alt="Business Skills Certificate digital badge"
              className="consulting-advisory-hero-badge-img"
              loading="lazy"
            />
          </div>
        </header>

        <section className="consulting-advisory-section" aria-label={t("consultingAdvisoryPage.a11y.problem")}
        >
          <Row className="consulting-advisory-split" xs={1} md={2}>
            <Col className="about-top-col">
              <div className="policy-card consulting-advisory-panel">
                <h2 className="consulting-advisory-heading consulting-advisory-heading--left">
                  <Trans i18nKey="consultingAdvisoryPage.headings.whatWeDo" components={{ purple: <span className="purple" /> }} />
                </h2>
                <p className="consulting-advisory-sublead consulting-advisory-sublead--left">
                  {t("consultingAdvisoryPage.whatWeDo.lead")}
                </p>

                <div className="consulting-advisory-quote">
                  <div className="consulting-advisory-quote-label">{t("consultingAdvisoryPage.problem.label")}</div>
                  <div className="consulting-advisory-quote-text">{t("consultingAdvisoryPage.problem.text")}</div>
                </div>

                <p className="consulting-advisory-muted">{t("consultingAdvisoryPage.problem.note")}</p>
              </div>
            </Col>

            <Col className="about-top-col">
              <div className="policy-card consulting-advisory-panel consulting-advisory-panel--accent">
                <h2 className="consulting-advisory-heading consulting-advisory-heading--left">
                  <Trans i18nKey="consultingAdvisoryPage.headings.promise" components={{ purple: <span className="purple" /> }} />
                </h2>

                <ul className="consulting-advisory-list consulting-advisory-list--tight">
                  {approachBullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>

                <div className="consulting-advisory-micro">{t("consultingAdvisoryPage.approach.micro")}</div>
              </div>
            </Col>
          </Row>
        </section>

        <section className="consulting-advisory-section" aria-label={t("consultingAdvisoryPage.a11y.coreAreas")}>
          <h2 className="consulting-advisory-heading">
            <Trans i18nKey="consultingAdvisoryPage.headings.coreAreas" components={{ purple: <span className="purple" /> }} />
          </h2>
          <p className="consulting-advisory-sublead">{t("consultingAdvisoryPage.coreAreas.lead")}</p>

          <div className="consulting-advisory-grid">
            {coreAreaItems.map((item) => (
              <div key={item.title} className="policy-card consulting-advisory-card">
                <div className="consulting-advisory-card-kicker">{item.kicker}</div>
                <h3 className="consulting-advisory-card-title">{item.title}</h3>
                <p className="consulting-advisory-card-lead">{item.lead}</p>
                <ul className="consulting-advisory-list">
                  {(Array.isArray(item.bullets) ? item.bullets : []).map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="consulting-advisory-section" aria-label={t("consultingAdvisoryPage.a11y.howToWork")}>
          <div className="consulting-advisory-stepper-header">
            <h2 className="consulting-advisory-heading consulting-advisory-heading--left">
              <Trans i18nKey="consultingAdvisoryPage.headings.howToWork" components={{ purple: <span className="purple" /> }} />
            </h2>
            <p className="consulting-advisory-sublead consulting-advisory-sublead--left">
              {t("consultingAdvisoryPage.howToWork.lead")}
            </p>
          </div>

          <div className="consulting-advisory-stepper" role="list">
            {offerItems.map((step) => (
              <div key={step.code} className={`policy-card consulting-advisory-step${step.isStart ? " consulting-advisory-step--start" : ""}`} role="listitem">
                <div className="consulting-advisory-step-top">
                  <div className="consulting-advisory-step-code">{step.code}</div>
                  <div className="consulting-advisory-step-badges">
                    {step.timeline ? <span className="consulting-advisory-badge">{step.timeline}</span> : null}
                    {step.price ? <span className="consulting-advisory-badge consulting-advisory-badge--price">{step.price}</span> : null}
                  </div>
                </div>

                <h3 className="consulting-advisory-step-title">{step.title}</h3>
                <p className="consulting-advisory-step-lead">{step.lead}</p>

                <ul className="consulting-advisory-list">
                  {(Array.isArray(step.bullets) ? step.bullets : []).map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>

                {step.note ? <div className="consulting-advisory-step-note">{step.note}</div> : null}
              </div>
            ))}
          </div>
        </section>

        <section className="consulting-advisory-section" aria-label={t("consultingAdvisoryPage.a11y.build")}>
          <Row className="consulting-advisory-split" xs={1} md={2}>
            <Col className="about-top-col">
              <div className="policy-card consulting-advisory-panel">
                <h2 className="consulting-advisory-heading consulting-advisory-heading--left">
                  <Trans i18nKey="consultingAdvisoryPage.headings.build" components={{ purple: <span className="purple" /> }} />
                </h2>
                <p className="consulting-advisory-sublead consulting-advisory-sublead--left">
                  {t("consultingAdvisoryPage.build.lead")}
                </p>

                <div className="consulting-advisory-chipset" aria-label={t("consultingAdvisoryPage.a11y.buildList")}>
                  {buildItems.map((label) => (
                    <span key={label} className="consulting-advisory-chip">
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </Col>

            <Col className="about-top-col">
              <div className="policy-card consulting-advisory-panel consulting-advisory-panel--cta">
                <h2 className="consulting-advisory-heading consulting-advisory-heading--left">
                  <Trans i18nKey="consultingAdvisoryPage.headings.start" components={{ purple: <span className="purple" /> }} />
                </h2>

                <p className="consulting-advisory-sublead consulting-advisory-sublead--left">
                  {t("consultingAdvisoryPage.start.lead")}
                </p>

                <Button
                  as="a"
                  href={CONSULTATION_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="custom-yellow-btn consulting-advisory-btn"
                >
                  {t("consultingAdvisoryPage.cta.book")}
                </Button>

                <div className="consulting-advisory-footnote">{t("consultingAdvisoryPage.start.footnote")}</div>
              </div>
            </Col>
          </Row>
        </section>
      </Container>
    </Container>
  );
}

export default ConsultingAdvisory;
